import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { OfficialBadge } from '../../components/OfficialBadge';
import { MessageCircle, Library, HelpCircle, ArrowRight, Users, Sparkles, ShieldCheck, BookOpen, Handshake, Ban, CheckCircle2 } from 'lucide-react';

export default function CommunityHub() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.adminCommunitySummary().catch(() => null).then(s => setSummary(s));
  }, []);

  const modules = [
    {
      to: '/reseau/forum',
      icon: MessageCircle,
      title: 'Forum thématique',
      desc: "8 catégories pour échanger entre moniteurs : pédagogie, plateau, circulation, CCS, mécanique, réglementation…",
      stat: summary ? `${summary.total_threads} discussion(s) · ${summary.total_posts} message(s)` : '',
      color: '#1d4ed8', soft: '#eef5ff'
    },
    {
      to: '/reseau/ressources',
      icon: Library,
      title: 'Banque de ressources',
      desc: "Partagez fiches de cours, exercices plateau, audio briefings, photos de situations. Chaque ressource est validée puis publiée.",
      stat: summary ? `${summary.total_resources_approved} ressource(s) publiée(s)` : '',
      color: '#16a34a', soft: '#eaf8ef'
    },
    {
      to: '/reseau/qcm',
      icon: HelpCircle,
      title: 'QCM communautaires',
      desc: "Proposez de nouvelles questions ETM. Validées par l'admin, elles enrichissent la base partagée. Auteur crédité.",
      stat: summary ? `${summary.total_community_questions_approved} question(s) intégrée(s)` : '',
      color: '#cf2031', soft: '#fff1f2'
    },
  ];

  return (
    <div className="space-y-8" data-testid="community-hub">
      <section className="mc-card p-6 sm:p-10 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full blur-3xl opacity-20" style={{ background: 'hsl(var(--primary))' }} />
        <div className="relative">
          <div className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary-soft))] px-3 py-1 text-xs font-bold text-[hsl(var(--primary-dark))] mb-3">
            <Users className="w-3.5 h-3.5" /> RÉSEAU PRO ECSR · accès validé
          </div>
          <h1 className="text-3xl sm:text-5xl font-black tracking-tight leading-[1.05]">
            La communauté des moniteurs<br />
            <span className="text-[hsl(var(--primary))]">qui font évoluer le métier.</span>
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-4 max-w-2xl leading-relaxed">
            Un espace fermé, modéré, réservé aux enseignants ECSR validés. Échangez vos pratiques, enrichissez la banque pédagogique, valorisez vos méthodes auprès de vos pairs.
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <OfficialBadge size="md" />
            <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest rounded-full bg-[#16a34a]/12 text-[#16a34a] border border-[#16a34a]/25 px-2.5 py-1">
              <ShieldCheck className="w-3.5 h-3.5" /> Modération admin
            </span>
          </div>
        </div>
      </section>

      <section className="grid md:grid-cols-3 gap-4" data-testid="hub-modules">
        {modules.map((m, i) => {
          const Icon = m.icon;
          return (
            <button
              key={i}
              onClick={() => navigate(m.to)}
              data-testid={`hub-module-${m.to.replace(/\W/g, '-')}`}
              className="mc-card p-5 text-left hover:-translate-y-0.5 hover:shadow-lg transition-all relative overflow-hidden"
            >
              <div className="absolute -top-10 -right-10 w-24 h-24 rounded-full blur-2xl opacity-20" style={{ background: m.color }} />
              <div className="relative">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3" style={{ background: m.soft, color: m.color }}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="font-black text-lg mb-1">{m.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{m.desc}</p>
                {m.stat && <div className="text-[10px] font-mono font-bold mt-3 text-foreground/70">{m.stat}</div>}
                <div className="mt-3 inline-flex items-center text-xs font-bold text-[hsl(var(--primary))]">
                  Accéder <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </div>
              </div>
            </button>
          );
        })}
      </section>

      <section className="mc-card p-5 sm:p-6 border-l-4 border-l-[hsl(var(--primary))]" data-testid="hub-charter">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))]">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className="font-black text-lg mb-2">Charte de la communauté</h3>
            <ul className="space-y-1.5 text-sm text-foreground/85">
              <li className="flex items-start gap-2"><BookOpen className="w-4 h-4 flex-shrink-0 mt-0.5 text-[hsl(var(--primary))]" /><span><strong>Sources fiables</strong> : argumenter avec des références au Code de la route, au programme officiel ETM ou au REMC.</span></li>
              <li className="flex items-start gap-2"><Handshake className="w-4 h-4 flex-shrink-0 mt-0.5 text-[hsl(var(--primary))]" /><span><strong>Bienveillance</strong> : on est tous passés par là. Les juniors posent des questions, les seniors partagent leur expérience.</span></li>
              <li className="flex items-start gap-2"><Ban className="w-4 h-4 flex-shrink-0 mt-0.5 text-[hsl(var(--primary))]" /><span><strong>Pas de pub commerciale</strong> sauvage. Pas d'auto-promotion intempestive d'auto-école concurrente.</span></li>
              <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5 text-[hsl(var(--primary))]" /><span><strong>Confidentialité élève</strong> : aucun nom, photo, plaque ou détail personnel d'un élève ne doit apparaître publiquement.</span></li>
              <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5 text-[hsl(var(--primary))]" /><span><strong>Modération</strong> : tout contenu peut être épinglé/verrouillé/supprimé par l'admin. Décisions souveraines.</span></li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
